import { useCallback, useEffect, useState } from 'react';
import { SUBSCRIPTION_VALIDATION_ENABLED } from '@/config/subscriptionPolicy';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { refreshSessionInit } from '@/store/sessionSlice';
import { isOrgOwner } from '@/utils/moduleAccess';
import type { FeatureCode } from './featureCodes';
import type { OrganizationFeatureDto } from '@/api/platform.api';

const CORE_FEATURES: FeatureCode[] = [
  'POS_BILLING',
  'INVENTORY',
  'EMPLOYEE_BASIC',
  'REPORTING',
  'PROCUREMENT',
];

/**
 * Feature gate helper — reads enabled features from session bootstrap (Redux auth slice).
 * No network call; use refreshSessionInit() after subscription changes.
 */
export function useFeatures() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const authFeatures = useAppSelector((s) => s.auth.enabledFeatures);
  const enabledModules = useAppSelector((s) => s.auth.enabledModules);
  const [loading, setLoading] = useState(false);

  const features: OrganizationFeatureDto[] = authFeatures.map((code) => ({
    code,
    name: code,
    module: enabledModules.includes(code) ? code : 'CORE',
    enabled: true,
    locked: false,
  }));

  const enabledSet = new Set(authFeatures);

  const hasFeature = useCallback(
    (code: FeatureCode | string) => {
      if (!SUBSCRIPTION_VALIDATION_ENABLED) return true;
      if (user?.masterAdmin) return true;
      if (isOrgOwner(user?.permissions ?? [], user?.roleCode)) return true;
      if (authFeatures.length > 0) {
        return enabledSet.has(code);
      }
      return CORE_FEATURES.includes(code as FeatureCode);
    },
    [user?.masterAdmin, user?.permissions, user?.roleCode, authFeatures, enabledSet],
  );

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await dispatch(refreshSessionInit(undefined));
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    setLoading(false);
  }, [authFeatures]);

  return { features, loading, refresh, hasFeature };
}
